import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { useGetProject } from '../../../shared/api/hooks/project/useGetProject';
import { PROJECTS } from '../../../shared/routes/path';
import { EffectiveFeaturesProvider } from '../../../shared/contexts/effective-features/EffectiveFeaturesContext';
import ProjectsPreviewPage from './ProjectsPreviewPage';
import ProjectsPreviewPageV2 from './ProjectsPreviewPageV2';

/**
 * Picks which Project Preview page to render based on the NEW_STATS addon.
 * Accounts with NEW_STATS see the redesigned V2 page; everyone else keeps the current page.
 * When a superadmin opens a CLIENT's project, the version follows the OWNER's addons
 * (project.ownerAddons) so it matches what the client actually sees. Same route/URL throughout.
 */
const ProjectsPreviewPageSwitch: React.FC = () => {
  const { projectId } = useParams();
  const { accountResponse } = useAccount();
  // Id z adresu bywa czymkolwiek (`/projects/dsadas/daily/all`) — bez tego sprawdzenia strona
  // ruszała z `NaN` i dopiero głęboko w dole coś się wywracało.
  const parsedProjectId = Number(projectId);
  const isValidProjectId = Number.isInteger(parsedProjectId) && parsedProjectId > 0;
  const { project, isLoading, isError } = useGetProject(isValidProjectId ? parsedProjectId : null);

  const isSuperAdmin = !!accountResponse?.isSuperAdmin;
  // ownerAddons is set only when a superadmin fetches a client's project → follow the OWNER's addon.
  const ownerAddons = project?.ownerAddons ?? null;
  const hasNewStats = !!(ownerAddons ?? accountResponse?.addons)?.includes('NEW_STATS');
  // Owner's models + addons when a superadmin views a client's project; otherwise the logged-in
  // account's — so both the model list and NEW_STATS-gated logic match the client's view.
  const effectiveFeatures = project?.ownerFeatures ?? accountResponse?.features ?? [];
  const effectiveAddons = ownerAddons ?? accountResponse?.addons ?? [];

  // For a superadmin, wait for the project before deciding, so we don't flash the superadmin's own
  // version (V1/V2) and then remount into the client's. Regular users never have ownerAddons, so their
  // decision is stable from `accountResponse` — no gating needed.
  if (isSuperAdmin && isLoading) {
    return null;
  }

  // Projekt nie istnieje albo nie mamy do niego dostępu (cudze konto, projekt spoza zakresu gościa,
  // skasowany). Zamiast pustej strony z błędami w każdym kafelku — z powrotem na listę projektów.
  if (!isValidProjectId || isError) {
    return <Navigate to={PROJECTS} replace />;
  }

  return (
    <EffectiveFeaturesProvider features={effectiveFeatures} addons={effectiveAddons}>
      {hasNewStats ? <ProjectsPreviewPageV2 /> : <ProjectsPreviewPage />}
    </EffectiveFeaturesProvider>
  );
};

export default ProjectsPreviewPageSwitch;
